import React, { useCallback, useEffect, useLayoutEffect } from 'react';
import type { ViewProps } from 'react-native';
import { StyleSheet, View } from 'react-native';
import { A } from '@expo/html-elements';
import {
  BodyText,
  checkIfRecaptchaEnabled,
  Form,
  TEST_GRECAPTCHA,
  TextLink,
  theme,
  useNoticeBannersStackContext,
  useReCaptcha,
} from '@sweetgreen/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { PageWrapper } from '@order/components';
import { useCustomer } from '@order/Customer';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useLocalizationContext } from '@order/Localization';

import {
  useCloseActiveScreensOnAuthStatusChange,
  useNavigateBack,
  useRedirectAfterAuthStateChange,
} from '../../navigation';
import type { JoinVerificationScreenProps } from '../../navigation/AppNavigation.props';
import {
  isEmailValid,
  useJoinVerificationForm,
} from './JoinVerificationScreen.hooks';
import { JoinVerificationScreenLoading } from './JoinVerificationScreen.loading';

// ────────────────────────────────────────────────────────────────────────────────

export const JoinVerificationScreen = (props: JoinVerificationScreenProps) => {
  const { navigation, route } = props;

  const {
    email: specifiedEmail,
    token: specifiedToken,
    redirect: redirectParam,
  } = route.params ?? {};

  const isLoggedIn = useIsLoggedIn();

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );
  const { redirectAfterSignIn } = useAuthFlow();
  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();

  const {
    isFetchingCustomer,
    verifySignUp: verify,
    resendVerificationCode: resendCode,
    isResendingVerificationCode,
    userSignUpVerificationEmail,
  } = useCustomer();

  const email = specifiedEmail ?? userSignUpVerificationEmail ?? '';
  const token = specifiedToken ?? '';

  const isValidEmailProvided = isEmailValid(email);

  const navigateBack = useNavigateBack();

  useLayoutEffect(() => {
    if (isFetchingCustomer) return;

    if (isLoggedIn && shouldUseAuthFlowRework) {
      redirectAfterAuthStateChange();

      return;
    }

    if (isLoggedIn && !shouldUseAuthFlowRework) {
      redirectAfterSignIn(redirectParam);

      return;
    }

    if (isValidEmailProvided) return;

    navigateBack();
  }, [
    navigation,
    isValidEmailProvided,
    isFetchingCustomer,
    isLoggedIn,
    redirectParam,
    redirectAfterSignIn,
    navigateBack,
    shouldUseAuthFlowRework,
    redirectAfterAuthStateChange,
  ]);

  // ────────────────────────────────────────────────────────────────────────────────

  if (isFetchingCustomer)
    return <JoinVerificationScreenLoading testID="join-verification-loading" />;

  return (
    <JoinVerificationForm
      testID="join-verification-form"
      email={email}
      token={token}
      verify={verify}
      resendCode={resendCode}
      isValidEmailProvided={isValidEmailProvided}
      isResendCodeLinkEnabled={!isResendingVerificationCode}
    />
  );
};

export const JoinVerificationForm = (props: JoinVerificationFormProps) => {
  const {
    testID,
    email,
    token,
    verify,
    resendCode,
    isValidEmailProvided,
    isResendCodeLinkEnabled,
  } = props;

  const { t } = useLocalizationContext();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();

  const shouldUseAuthFlowRework = useFeatureFlag(
    'cels-763-short-rework-the-authentication-redirect-flow-enabled',
  );
  const closeActiveScreensOnAuthStatusChange =
    useCloseActiveScreensOnAuthStatusChange();

  // ─── ReCaptcha ───────────────────────────────────────────────────

  const { reCaptchaMachine, reCaptchaRef } = useReCaptcha();

  const [reCaptchaState] = reCaptchaMachine;
  const isLoadingRecaptcha = reCaptchaState.matches('loading');
  const isExecutingRecaptcha = reCaptchaState.matches('executing');

  // ────────────────────────────────────────────────────────────────────

  const { form } = useJoinVerificationForm({
    defaultValues: { code: token },
  });
  const { control, formState, handleSubmit } = form;
  const { errors, isSubmitting } = formState;

  const shouldAutoVerify = isValidEmailProvided && token.length > 1;

  // ────────────────────────────────────────────────────────────────────

  const onSubmitValid = React.useCallback(
    async (values) => {
      const verifySignUpResponse = await verify({ ...values, email });

      const isSuccessfulVerification =
        verifySignUpResponse?.__typename === 'VerifyRegistrationSuccess';

      // close other open screens and modals, to prevent unnecessary network calls and state updates
      if (isSuccessfulVerification && shouldUseAuthFlowRework)
        closeActiveScreensOnAuthStatusChange();
    },
    [
      closeActiveScreensOnAuthStatusChange,
      email,
      shouldUseAuthFlowRework,
      verify,
    ],
  );

  const submitForm = React.useMemo(
    () => handleSubmit(onSubmitValid),
    [handleSubmit, onSubmitValid],
  );

  const resendVerificationCode = React.useCallback(
    async (grecaptcha = TEST_GRECAPTCHA) => {
      if (!isValidEmailProvided) return;

      await resendCode({ email, grecaptcha });

      // reset recaptcha after request
      reCaptchaRef.current?.resetRecaptcha();
    },
    [email, isValidEmailProvided, reCaptchaRef, resendCode],
  );

  const onResendVerificationCode = React.useCallback(async () => {
    if (!isValidEmailProvided) return;

    const isRecaptchaEnabled = checkIfRecaptchaEnabled();

    await (isRecaptchaEnabled
      ? reCaptchaRef.current?.executeRecaptcha()
      : resendVerificationCode());
  }, [isValidEmailProvided, reCaptchaRef, resendVerificationCode]);

  const onRecaptchaError = useCallback(() => {
    addNoticeBanner({ text: t('general.error'), palette: 'caution' });
  }, [addNoticeBanner, t]);

  // ────────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (shouldAutoVerify) void submitForm();
  }, [shouldAutoVerify, submitForm]);

  // ─────────────────────────────────────────────────────────────────

  const shouldDisableResendCodeBtn =
    !isResendCodeLinkEnabled || isLoadingRecaptcha || isExecutingRecaptcha;

  // ─────────────────────────────────────────────────────────────────

  return (
    <PageWrapper>
      <Form.Container
        testID={testID}
        reCaptchaRef={reCaptchaRef}
        reCaptchaMachine={reCaptchaMachine}
        onRecaptchaVerify={resendVerificationCode}
        onRecaptchaError={onRecaptchaError}
      >
        <Form.Title>{t('join-verification.title')}</Form.Title>

        <Form.Subtitle>
          <BodyText size={3}>
            {t('join-verification.subtitle', { email })}
          </BodyText>
        </Form.Subtitle>

        <Form.NumberFieldCode
          name="code"
          testID="field-code"
          control={control}
          rules={{ required: true }}
          label={t('form.enter-code')}
          notice={errors.code?.message}
          maxLength={6}
          onSubmit={submitForm}
          hideSubmitButton
        />

        <Form.SubmitButton
          testID="button-verify"
          isLoading={isSubmitting}
          onPress={submitForm}
        >
          {t('general.continue')}
        </Form.SubmitButton>

        <View style={styles.linksWrapper}>
          <View style={styles.linksRow}>
            <BodyText size={4}>
              {t('join-verification.dont-see-an-email')}{' '}
            </BodyText>

            <TextLink
              testID="link-resend-code"
              onPress={onResendVerificationCode}
              disabled={shouldDisableResendCodeBtn}
              size={4}
            >
              {t('general.resend')}
            </TextLink>
          </View>

          <View style={styles.linksRow}>
            <BodyText size={4}>
              {t('join-verification.having-trouble')}{' '}
            </BodyText>

            <A href={`mailto:${SG_SUPPORT_EMAIL}`}>
              <TextLink size={4}>{SG_SUPPORT_EMAIL}</TextLink>
            </A>
          </View>
        </View>
      </Form.Container>
    </PageWrapper>
  );
};

// ─── TYPES ──────────────────────────────────────────────────────────────────────

type JoinVerificationFormProps = {
  readonly email: string;
  readonly token: string;
  readonly verify: ReturnType<typeof useCustomer>['verifySignUp'];
  readonly resendCode: ReturnType<typeof useCustomer>['resendVerificationCode'];
  readonly isResendCodeLinkEnabled: boolean;
  readonly isValidEmailProvided: boolean;
} & Pick<ViewProps, 'testID'>;

// ─── STYLES ─────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  linksWrapper: {
    paddingTop: theme.spacing.xlarge,
  },
  linksRow: {
    flexDirection: 'row',
    paddingBottom: theme.spacing.small,
  },
});

// ─── CONSTANTS ──────────────────────────────────────────────────────────────────

const SG_SUPPORT_EMAIL = 'hello@sweetgreen.com';
